import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../api/auth';
import { TwoFactorSetup, TwoFactorSetupRef } from '../components/auth/TwoFactorSetup';
import { BackupCodesDisplay } from '../components/auth/BackupCodesDisplay';
import { TwoFactorStatusResponse } from '../types';

export const Profile: React.FC = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isLoading: authLoading, updateUser, logout } = useAuth();

  // Profile form state
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Password dialog state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // 2FA state
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatusResponse | null>(null);
  const [isLoading2FA, setIsLoading2FA] = useState(true);
  const [showTwoFactorDialog, setShowTwoFactorDialog] = useState(false);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [regeneratedCodes, setRegeneratedCodes] = useState<string[] | null>(null);

  // 2FA Setup dialog state
  const setupRef = useRef<TwoFactorSetupRef>(null);
  const [setupStep, setSetupStep] = useState(0);
  const [setupState, setSetupState] = useState({ isVerifying: false, canVerify: false });

  // Sync fullName when user data loads
  useEffect(() => {
    if (user?.full_name) {
      setFullName(user.full_name);
    }
  }, [user?.full_name]);

  // Load 2FA status
  useEffect(() => {
    const load2FAStatus = async () => {
      try {
        const status = await authApi.get2FAStatus();
        setTwoFactorStatus(status);
      } catch {
        // Status will remain null
      } finally {
        setIsLoading2FA(false);
      }
    };
    load2FAStatus();
  }, []);

  // Auto-open 2FA setup dialog if query parameter is present
  useEffect(() => {
    if (searchParams.get('setup2fa') === 'true' && !isLoading2FA && !twoFactorStatus?.enabled) {
      setShowSetupDialog(true);
      // Remove the query parameter to avoid re-opening on refresh
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, isLoading2FA, twoFactorStatus?.enabled, setSearchParams]);

  if (authLoading) {
    return (
      <div className="loading-container">
        <ProgressSpinner />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const handleSaveProfile = async () => {
    setProfileError(null);

    if (!fullName.trim()) {
      setProfileError('Name is required');
      return;
    }

    setIsSavingProfile(true);

    try {
      await authApi.updateProfile({ full_name: fullName.trim() });
      await updateUser();
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Profile updated successfully',
        life: 3000,
      });
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Failed to update profile';
      setProfileError(message);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);

    if (!currentPassword) {
      setPasswordError('Current password is required');
      return;
    }

    if (!newPassword) {
      setPasswordError('New password is required');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setIsSavingPassword(true);

    try {
      await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });

      toast.current?.show({
        severity: 'success',
        summary: 'Password Changed',
        detail: 'Your password has been changed. You will be logged out.',
        life: 3000,
      });

      closePasswordDialog();

      setTimeout(async () => {
        await logout();
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      let message = 'Failed to change password';
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        message = detail[0].msg || 'Validation error';
      }
      setPasswordError(message);
    } finally {
      setIsSavingPassword(false);
    }
  };

  const closePasswordDialog = () => {
    setShowPasswordDialog(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handle2FASetupComplete = async () => {
    setShowSetupDialog(false);
    setShowTwoFactorDialog(false);
    const status = await authApi.get2FAStatus();
    setTwoFactorStatus(status);
    toast.current?.show({
      severity: 'success',
      summary: 'Success',
      detail: 'Two-factor authentication has been enabled',
      life: 3000,
    });
  };

  const handleRegenerateBackupCodes = async () => {
    if (regenerateCode.length !== 6) {
      setRegenerateError('Please enter a 6-digit code');
      return;
    }

    setIsRegenerating(true);
    setRegenerateError(null);

    try {
      const response = await authApi.regenerateBackupCodes(regenerateCode);
      setRegeneratedCodes(response.backup_codes);
      const status = await authApi.get2FAStatus();
      setTwoFactorStatus(status);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid verification code';
      setRegenerateError(message);
    } finally {
      setIsRegenerating(false);
    }
  };

  const closeRegenerateDialog = () => {
    setShowRegenerateDialog(false);
    setRegenerateCode('');
    setRegenerateError(null);
    setRegeneratedCodes(null);
  };

  const hasProfileChanges = fullName.trim() !== (user.full_name || '');

  return (
    <>
      <Toast ref={toast} />
      <div className="page-container">
        <div className="page-header">
          <div className="controls-panel spaced-out">
            <h2>Profile
            <span
              title={twoFactorStatus?.enabled ? "2 factor enabled" : "2 factor disabled"}
              className={
                twoFactorStatus?.enabled
                  ? "badge-success ms-out ms-out-shield-lock"
                  : "badge-danger ms-out ms-out-shield-lock"
              }
            ></span>
            </h2>
            <div className="controls-group">
              <Button
                label="Change Password"
                icon="ms-out ms-out-lock"
                size="small"
                link
                onClick={() => setShowPasswordDialog(true)}
              />

            {!twoFactorStatus?.enabled &&
              <Button
                label='Set Up 2FA'
                icon='ms-out ms-out-shield'
                severity='warning'
                size="small"
                link
                onClick={() => setShowSetupDialog(true)}
              />
            }
            {twoFactorStatus?.enabled &&
              <Button
                label="Regenerate Backup Codes"
                icon="ms-out ms-out-refresh"
                size="small"
                link
                onClick={() => setShowRegenerateDialog(true)}
              />
            }
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="form-field">
            <label htmlFor="fullName">Full Name</label>
            <InputText
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={isSavingProfile}
              placeholder="Enter your name"
            />
          </div>

          <div className="form-field">
            <label htmlFor="email">Email</label>
            <InputText id="email" value={user.email} disabled className="p-disabled" />
            <small className="field-help">Email cannot be changed</small>
          </div>

          <div className="form-field">
            <label>Member Since</label>
            <p className="field-value">{formatDate(user.created_at)}</p>
          </div>

          {user.organizations && user.organizations.length > 0 && (
            <div className="form-field">
              <label>Organizations</label>
              <ul className="org-list-inline">
                {user.organizations.map((userOrg) => (
                  <li key={userOrg.id}>{userOrg.organization.name}</li>
                ))}
              </ul>
            </div>
          )}

          {profileError && <Message severity="error" text={profileError} className="field-message" />}

          <div className="field-actions">
            <Button
              label="Save Changes"
              icon="ms-out ms-out-save"
              onClick={handleSaveProfile}
              loading={isSavingProfile}
              disabled={isSavingProfile || !hasProfileChanges}
            />
          </div>
        </div>

        {/* Change Password Dialog */}
        <Dialog
          header="Change Password"
          visible={showPasswordDialog}
          onHide={closePasswordDialog}
          className="profile-dialog"
          closable
          draggable={false}
          footer={
            <div className="dialog-footer">
              <Button
                label="Cancel"
                onClick={closePasswordDialog}
                disabled={isSavingPassword}
              />
              <Button
                label="Change Password"
                icon="ms-out ms-out-lock"
                onClick={handleChangePassword}
                severity="success"
                loading={isSavingPassword}
                disabled={isSavingPassword || !currentPassword || !newPassword || !confirmPassword}
              />
            </div>
          }
        >
          <div className="dialog-form">
            <div className="form-field">
              <label htmlFor="currentPassword">Current Password</label>
              <Password
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={isSavingPassword}
                toggleMask
                feedback={false}
                placeholder="Enter current password"
              />
            </div>

            <div className="form-field">
              <label htmlFor="newPassword">New Password</label>
              <Password
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isSavingPassword}
                toggleMask
                feedback={false}
                placeholder="Enter new password"
              />
            </div>

            <div className="form-field">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <Password
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSavingPassword}
                toggleMask
                feedback={false}
                placeholder="Confirm new password"
              />
            </div>

            {passwordError && <Message severity="error" text={passwordError} />}
          </div>
        </Dialog>

        {/* Two-Factor Authentication Status Dialog */}
        <Dialog
          header="Two-Factor Authentication"
          visible={showTwoFactorDialog}
          onHide={() => setShowTwoFactorDialog(false)}
          className="profile-dialog"
          closable
          draggable={false}
          footer={
            !isLoading2FA && (
              <div className="dialog-footer">
                {twoFactorStatus?.enabled ? (
                  <Button
                    label="Regenerate Backup Codes"
                    icon="ms-out ms-out-refresh"
                    onClick={() => {
                      setShowTwoFactorDialog(false);
                      setShowRegenerateDialog(true);
                    }}
                  />
                ) : (
                  <Button
                    label="Enable 2 Factor Auth"
                    icon="ms-out ms-out-security"
                    severity="success"
                    onClick={() => setShowSetupDialog(true)}
                  />
                )}
              </div>
            )
          }
        >
          {isLoading2FA ? (
            <div className="dialog-loading">
              <ProgressSpinner />
            </div>
          ) : twoFactorStatus?.enabled ? (
            <div className="two-factor-status">
              <div className="status-row">
                <span className="status-label">Status</span>
                <span className="status-value status-enabled">
                  <i className="ms-out ms-out-check-circle" /> Enabled
                </span>
              </div>
              <div className="status-row">
                <span className="status-label">Backup Codes</span>
                <span className="status-value">
                  {twoFactorStatus.backup_codes_remaining} of 8 remaining
                </span>
              </div>
            </div>
          ) : (
            <div className="two-factor-status">
              <div className="status-row">
                <span className="status-label">Status</span>
                <span className="status-value status-disabled">Not enabled</span>
              </div>
              <p className="two-factor-description">
                Add an extra layer of security to your account by enabling 
                two-factor authentication using an authenticator app.
              </p>
            </div>
          )}
        </Dialog>

        {/* 2FA Setup Dialog */}
        <Dialog
          header="Set Up Two-Factor Authentication"
          visible={showSetupDialog}
          onHide={() => setShowSetupDialog(false)}
          className="two-factor-dialog"
          closable
          draggable={false}
          footer={
            <div className="dialog-footer">
              {setupStep === 0 && (
                <>
                  <Button
                    label="Cancel"
                    onClick={() => setShowSetupDialog(false)}
                  />
                  <Button
                    label="Next"
                    icon="ms-out ms-out-arrow-forward"
                    iconPos="right"
                    severity="success"
                    onClick={() => setupRef.current?.goToStep(1)}
                  />
                </>
              )}
              {setupStep === 1 && (
                <>
                  <Button
                    label="Back"
                    onClick={() => setupRef.current?.goToStep(0)}
                    disabled={setupState.isVerifying}
                  />
                  <Button
                    label="Verify & Enable"
                    icon="ms-out ms-out-check"
                    severity="success"
                    onClick={() => setupRef.current?.verify()}
                    disabled={!setupState.canVerify || setupState.isVerifying}
                    loading={setupState.isVerifying}
                  />
                </>
              )}
              {setupStep === 2 && (
                <Button
                  label="I've saved my codes"
                  icon="ms-out ms-out-check"
                  severity="success"
                  onClick={handle2FASetupComplete}
                />
              )}
            </div>
          }
        >
          <TwoFactorSetup
            ref={setupRef}
            onStepChange={setSetupStep}
            onStateChange={setSetupState}
          />
        </Dialog>

        {/* Regenerate Backup Codes Dialog */}
        <Dialog
          header="Regenerate Backup Codes"
          visible={showRegenerateDialog}
          onHide={closeRegenerateDialog}
          className="two-factor-dialog"
          closable
          draggable={false}
          footer={
            <div className="dialog-footer">
              {!regeneratedCodes ? (
                <>
                  <Button
                    label="Cancel"
                    onClick={closeRegenerateDialog}
                    disabled={isRegenerating}
                  />
                  <Button
                    label="Regenerate"
                    icon="ms-out ms-out-refresh"
                    severity="success"
                    onClick={handleRegenerateBackupCodes}
                    disabled={regenerateCode.length !== 6 || isRegenerating}
                    loading={isRegenerating}
                  />
                </>
              ) : (
                <Button
                  label="I've saved my codes"
                  icon="ms-out ms-out-check"
                  severity="success"
                  onClick={closeRegenerateDialog}
                />
              )}
            </div>
          }
        >
          {regeneratedCodes ? (
            <BackupCodesDisplay codes={regeneratedCodes} />
          ) : (
            <>
              <Message text="WARNING: This will invalidate all your existing backup codes." severity="warn"></Message>

              <p>Enter your 6-digit authenticator code to generate new backup codes.</p>

              {regenerateError && <Message severity="error" text={regenerateError} />}

              <div className="two-factor-code-input">
                <InputText
                  value={regenerateCode}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setRegenerateCode(cleaned);
                    setRegenerateError(null);
                  }}
                  placeholder="000000"
                  maxLength={6}
                  disabled={isRegenerating}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && regenerateCode.length === 6) {
                      handleRegenerateBackupCodes();
                    }
                  }}
                />
              </div>
            </>
          )}
        </Dialog>
      </div>
    </>
  );
};

export default Profile;
